import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Box,
  Badge,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { useOktaAuth } from '@okta/okta-react';
import { useNavigate } from 'react-router-dom';
import { LAYOUT } from '../../utils/layoutConstants';
import { FONT_FAMILY_PRIMARY } from '../../constants/fonts';
import './JLLHeader.css';

const JLLHeader = ({ sidebarOpen = true }) => {
  const { authState, oktaAuth } = useOktaAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchor, setNotificationAnchor] = useState(null);

  const userName = authState?.idToken?.claims?.name || 
                   authState?.idToken?.claims?.given_name || 
                   'User';
  const userEmail = authState?.idToken?.claims?.email || '';
  
  const initials = userName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = (event) => {
    setNotificationAnchor(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationAnchor(null);
  };

  const handleLogout = async () => {
    try {
      await oktaAuth.signOut();
      navigate('/signin');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <AppBar 
      position="fixed" 
      elevation={0}
      sx={{ 
        backgroundColor: '#FFFFFF',
        color: '#000000',
        borderBottom: '6px solid #f5f5f5',
        left: 0,
        right: 0,
        width: '100%',
        zIndex: 1300,
        top: 0,
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '1px',
          backgroundColor: '#E0E0E0',
        },
      }}
    >
      <Toolbar 
        sx={{ 
          minHeight: LAYOUT.HEADER_HEIGHT, 
          height: LAYOUT.HEADER_HEIGHT,
          pl: '31px',
          pr: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box 
          sx={{ 
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Typography
            variant="h4"
            component="div"
            sx={{
              fontFamily: 'Source Sans Pro, Arial, sans-serif',
              fontWeight: 700,
              fontSize: '1.75rem',
              lineHeight: 1.2,
              color: '#000000',
              whiteSpace: 'nowrap',
            }}
          >
            JLL <span style={{ color: '#40798d' }}>SDA</span>
          </Typography>
          <Typography
            variant="body2"
            component="div"
            sx={{
              fontFamily: FONT_FAMILY_PRIMARY,
              fontWeight: 400,
              color: '#666666',
              fontSize: '0.875rem',
              whiteSpace: 'nowrap',
            }}
          >
            Turning responses into strategic insights
          </Typography>
        </Box>

        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            flexShrink: 0,
          }}
        >
        

          <IconButton
            onClick={handleNotificationClick}
            sx={{
              color: '#000000',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <Badge badgeContent={0} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>

          <IconButton
            onClick={handleProfileMenuOpen}
            sx={{
              p: 0,
              '&:hover': {
                opacity: 0.8,
              },
            }}
          >
            <Avatar
              sx={{
                bgcolor: '#000000',
                width: 40,
                height: 40,
                fontSize: '1rem',
              }}
            >
              {initials}
            </Avatar>
          </IconButton>

          <IconButton
            onClick={handleLogout}
            sx={{
              color: '#000000',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
            title="Logout"
          >
            <LogoutIcon />
          </IconButton>
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleProfileMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{
            sx: {
              mt: 1.5,
              minWidth: 200,
              borderRadius: '0.5rem',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            },
          }}
        >
          <MenuItem disabled>
            <Box>
              <Typography variant="body2" fontWeight={600}>
                {userName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {userEmail}
              </Typography>
            </Box>
          </MenuItem>
        </Menu>

        <Menu
          anchorEl={notificationAnchor}
          open={Boolean(notificationAnchor)}
          onClose={handleNotificationClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{
            sx: {
              mt: 1.5,
              minWidth: 300,
              borderRadius: '0.5rem',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            },
          }}
        >
          <MenuItem disabled>
            <Typography variant="body2" fontWeight={600}>
              Notifications
            </Typography>
          </MenuItem>
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              No new notifications
            </Typography>
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default JLLHeader;
